import { create } from "zustand";
import { streamTTS, CurrentVoiceId } from "@/lib/minimaxTTS";
import supabase from "@/lib/supabase";
import STORAGE_KEYS from "@/constant/storage";
import { stripNonTextCharacters } from "@/lib/utils";
import useAuthStore from "./useAuthStore";
import report, { ReportEvent } from "@/lib/report";

// LaTeX detection and processing functions
const hasLatex = (text: string): boolean => {
  // Detect block-level formulas $$...$$
  const blockLatexRegex = /\$\$[\s\S]*?\$\$/g;
  if (blockLatexRegex.test(text)) {
    return true;
  }
  
  // Detect inline formulas $...$, but avoid false identification of monetary amounts
  const inlineLatexRegex = /\$([^$\n]+)\$/g;
  const matches = text.match(inlineLatexRegex);
  
  if (!matches) return false;
  
  // Check if the matched content contains mathematical formula features
  for (const match of matches) {
    const content = match.slice(1, -1); // Remove leading and trailing $ symbols
    
    // Mathematical formula feature detection
    const mathFeatures = [
      // Mathematical symbols
      /[+\-*/=<>≤≥≠∞∑∏∫∂∇]/,
      // Superscripts and subscripts
      /[_^]/,
      // Greek letters
      /\\?(alpha|beta|gamma|delta|epsilon|zeta|eta|theta|iota|kappa|lambda|mu|nu|xi|pi|rho|sigma|tau|upsilon|phi|chi|psi|omega)/i,
      // Mathematical functions
      /\\?(sin|cos|tan|log|ln|exp|sqrt|frac|sum|int|lim|max|min)/i,
      // Fraction form
      /\\frac\{.*?\}\{.*?\}/,
      // Square root
      /\\sqrt/,
      // Bracket combinations
      /\\[lr]?[()[\]{}]/,
      // Multiple letter variable combinations (usually mathematical)
      /[a-zA-Z]{2,}[_^]?[0-9]*/
    ];
    
    // If it contains any mathematical features, consider it a LaTeX formula
    if (mathFeatures.some(pattern => pattern.test(content))) {
      return true;
    }
    
    // If it only contains numbers and decimal points, it might be money, not LaTeX
    if (/^\d+(\.\d+)?$/.test(content)) {
      continue;
    }
    
    // If it contains combinations of letters and numbers, it might be mathematical variables
    if (/[a-zA-Z]/.test(content) && /[0-9_^]/.test(content)) {
      return true;
    }
  }
  
  return false;
};

// LLM processing function: convert text containing LaTeX to natural language
const convertLatexToNaturalLanguage = async (text: string): Promise<string | null> => {
  try {
    const { apiClient } = await import('@/lib/api-client');
    
    const response = await apiClient.post('/api/ai/convert-latex', {
      text: text,
      prompt: `Convert the following text containing LaTeX mathematical formulas into natural language suitable for text-to-speech. Keep the main meaning of the original text, but convert mathematical formulas into spoken descriptions. Examples:
- $x^2$ becomes "x squared"
- $\\frac{1}{2}$ becomes "one half"  
- $\\sum_{i=1}^{n}$ becomes "sum from i equals 1 to n"
- $\\int_{0}^{\\infty}$ becomes "integral from 0 to infinity"
- $\\alpha$ becomes "alpha"
- $e^{-x}$ becomes "e to the negative x"

Please return only the converted text in English, without any additional explanations.

Original text: ${text}`
    });

    if (!response.ok) {
      throw new Error('LLM API call failed');
    }

    const data = await response.json();
    return data.result || null;
  } catch (error) {
    console.error('Error converting LaTeX with LLM:', error);
    return null; // Return null when LLM fails, no fallback processing
  }
};

// Text processing helper function: replace em dash and en dash with commas, keep hyphen for linking
const convertDashesToCommas = (text: string): string => {
  // Only handle em dash (—) and en dash (–), not regular hyphen (-)
  return text.replace(/[—–]/g, ', ');
};

// Text check function: return null if more than 3 sentences, indicating no playback
const trimTextToFirstSentence = (text: string): string | null => {
  try {
    // Use Compromise library to accurately split sentences
    const nlp = require('compromise');
    const doc = nlp(text);
    const sentences = doc.sentences().out('array');
    
    if (sentences.length > 5) {
      // If more than 3 sentences, return null to indicate no playback
      return null;
    }
    
    return text;
  } catch (error) {
    console.error('Error processing text with Compromise:', error);
    // If Compromise processing fails, fall back to simple regex
    const sentences = text.split(/[.!?。！？]+/).filter(s => s.trim().length > 0);
    
    if (sentences.length > 3) {
      return null;
    }
    
    return text;
  }
};

type PLAYING_TYPE = 'lesson_summary' | 'flashcard' | 'assistant' | 'dictation' | 'guide' | 'pet';
type TTS_STATUS = 'idle' | 'collecting' | 'playing' | 'finished' | 'aborted';

interface PlayTTSOptions {
  voiceId?: string;
  forcePlay?: boolean;
  needCache?: boolean;
  speed?: number;
}

type TTSTimingPayload = Record<string, number> | null;

interface AudioStore {
  isPlaying: boolean;
  isMuted: boolean;
  // Used to mark the type of audio currently being played
  playingType: PLAYING_TYPE;
  playingId: string;
  playingText: string;
  ttsStatus: TTS_STATUS;
  voiceId: string;

  play: (url: string, forcePlay?: boolean, volumeMultiplier?: number) => void;
  pause: () => void;
  stop: () => void;
  unload: () => void;
  setVolume: (volume: number) => void;
  mute: () => void;
  unmute: () => void;
  setOnEnded: (callback: () => void) => void;
  chatTTS: (text: string) => Promise<void>;
  playTTS: (text: string, options?: PlayTTSOptions) => Promise<void>;
  playPollyTTS: (text: string, voiceId?: string, forcePlay?: boolean) => Promise<void>;
  preloadPollyTTS: (text: string, voiceId?: string) => Promise<void>;
  toggleMute: () => void;
  interruptAudio: () => void;
  getAndClearTTSTiming: () => TTSTimingPayload;
  setPlayingType: (type: PLAYING_TYPE) => void;
  unlockAudioForIOS: () => Promise<void>;
  initVoiceId: (voiceIdParam: string) => Promise<string>;
}

// Read mute state from localStorage, default to false (not muted)
const getInitialMutedState = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const savedState = localStorage.getItem(STORAGE_KEYS.AUDIO_SETTING_MUTED);
  
  // If savedState is 'true', return true (muted), otherwise return false (not muted)
  return savedState === 'true';
};

// Generate a tiny silent WAV (about 20ms) for iOS audio unlock
const makeTinySilentWavDataURL = (): string => {
  const sampleRate = 8000;
  const samples = 160; // About 20ms of silence
  const dataSize = samples * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  let p = 0;

  const writeStr = (s: string) => { 
    for (let i = 0; i < s.length; i++) view.setUint8(p++, s.charCodeAt(i)); 
  };
  const u16 = (v: number) => { view.setUint16(p, v, true); p += 2; };
  const u32 = (v: number) => { view.setUint32(p, v, true); p += 4; };

  // RIFF header
  writeStr("RIFF"); u32(36 + dataSize); writeStr("WAVE");
  // fmt chunk
  writeStr("fmt "); u32(16); u16(1); u16(1); u32(sampleRate); u32(sampleRate * 2); u16(2); u16(16);
  // data chunk
  writeStr("data"); u32(dataSize);
  // PCM zeros (silence) already in buffer
  
  const bytes = new Uint8Array(buffer);
  const base64 = btoa(String.fromCharCode(...bytes));
  return "data:audio/wav;base64," + base64;
};

const useAudioStore = create<AudioStore>((set, get) => {
  // Audio context for Web Audio API
  let audioContext: AudioContext | null = null;
  let gainNode: GainNode | null = null;
  let currentVolume = 1.0;
  let isMuted = getInitialMutedState();
  let onEndedCallback: (() => void) | null = null;

  // Queue and state management
  let chunkQueue: Uint8Array[] = [];
  let lastTTSTiming: TTSTimingPayload = null;
  let isStreamingTTS = false;
  let isCollectingFinished = false;
  let isAborting = false;
  let abortController: AbortController | null = null;

  // Add global audio reference
  let currentAudio: HTMLAudioElement | null = null;

  // iOS audio unlock state
  let iosAudioUnlocked = false;
  let unlockAudioElement: HTMLAudioElement | null = null;

  // Two-tier cache: Memory -> Supabase
  // Note: We don't use localStorage to avoid caching expired AWS Polly URLs
  const memoryCache = new Map<string, string>();
  const CACHE_KEY_PREFIX = 'tts_cache_';
  
  // Public method: trigger callback
  const triggerEndCallback = (delay = 0) => {
    if (onEndedCallback) {
      const callback = onEndedCallback;
      onEndedCallback = null;
      if (delay > 0) {
        setTimeout(callback, delay);
      } else {
        callback();
      }
    }
  };
  
  // Public method: check if playback should be skipped
  const shouldSkipPlayback = (forcePlay = false): boolean => {
    if (isMuted && !forcePlay) {
      console.log("Audio muted, skipping playback");
      set({ isPlaying: true, ttsStatus: 'playing' });
      setTimeout(() => {
        triggerEndCallback();
        set({ isPlaying: false, playingText: '', ttsStatus: 'finished' });
      }, 500);
      return true;
    }
    return false;
  };

  // Public method: cleanup Audio element
  const cleanupAudioElement = () => {
    if (currentAudio) {
      try {
        currentAudio.pause();
        currentAudio.src = "";
        currentAudio.load(); // Reset audio element and release resources
      } catch (error) {
        console.error('Error stopping previous audio:', error);
      }
      currentAudio = null;
    }
  };

  // Initialize AudioContext if needed
  const initializeAudio = () => {
    try{
      if (!audioContext) {
        audioContext = new AudioContext();
        gainNode = audioContext.createGain();
        gainNode.connect(audioContext.destination);
        gainNode.gain.value = currentVolume;
  
        if (audioContext?.state === 'suspended') {
          audioContext.resume();
        }
      }
      return true;
    } catch (error) {
      console.error("Error getting AudioContext:", error);
      return false;
    }
  };

  // Complete cleanup of all resources
  const cleanup = () => {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    isAborting = true;
    isStreamingTTS = false;
    isCollectingFinished = false;
    chunkQueue = [];

    if (audioContext) {
      audioContext.close().catch(err => console.error('Error closing audio context:', err));
      audioContext = null;
      gainNode = null;
    }

    set({ isPlaying: false, playingText: '', ttsStatus: 'idle' });
  };
  
  // Handle playback errors
  const handlePlaybackError = (error: any) => {
    console.error('Error playing audio:', error);
    // Don't automatically toggle mute on error - this causes unexpected muting behavior
    // Users should manually control mute state through the UI
    set({ isPlaying: false, playingText: '', ttsStatus: 'idle' });
    currentAudio = null;
  };
  
  // Helper: Generate cache key
  const getCacheKey = (text: string, voiceId: string): string => {
    return `${CACHE_KEY_PREFIX}${voiceId}_${text}`;
  };

  // Public method: Two-tier cache lookup with timeout
  const checkTTSCache = async (text: string, voiceId: string): Promise<string | null> => {
    const cacheKey = getCacheKey(text, voiceId);

    try {
      // Tier 1: Memory cache (instant)
      if (memoryCache.has(cacheKey)) {
        console.log('[TTS Cache] ✓ Hit in memory cache (0ms)');
        return memoryCache.get(cacheKey)!;
      }

      // Tier 2: Supabase (with timeout)
      const CACHE_TIMEOUT = 2000; // 2 seconds timeout
      let timeoutId: NodeJS.Timeout;

      const timeoutPromise = new Promise<null>((resolve) => {
        timeoutId = setTimeout(() => {
          console.warn('[TTS Cache] Supabase lookup timed out after 2s');
          resolve(null);
        }, CACHE_TIMEOUT);
      });

      const cachePromise = supabase
        .from('tts_cache')
        .select('*')
        .eq('text', text)
        .eq('voice_id', voiceId)
        .maybeSingle();

      const result = await Promise.race([cachePromise, timeoutPromise]);

      // Clear timeout to prevent memory leak
      clearTimeout(timeoutId!);

      if (result === null) {
        return null;
      }

      const { data: cacheData } = result as any;
      const supabaseUrl = cacheData?.audio_url || null;

      if (supabaseUrl) {
        console.log('[TTS Cache] ✓ Hit in Supabase');
        // Populate memory cache
        memoryCache.set(cacheKey, supabaseUrl);
      }

      return supabaseUrl;
    } catch (error) {
      console.error("[TTS Cache] Error checking cache:", error);
      return null;
    }
  };

  // Helper: Save URL to memory cache only
  const saveToCaches = (text: string, voiceId: string, url: string): void => {
    const cacheKey = getCacheKey(text, voiceId);

    // Only save to memory cache
    memoryCache.set(cacheKey, url);

    console.log('[TTS Cache] ✓ Saved to memory cache');
  };

  // Play audio with Web Audio API for volume boost
  const playWithWebAudio = async (url: string, forcePlay: boolean, volumeMultiplier: number) => {
    const startTime = performance.now();
    console.log(`[WebAudio] 🎵 Starting playback process`);
    console.log(`[WebAudio] URL: ${url.substring(0, 100)}...`);
    console.log(`[WebAudio] forcePlay: ${forcePlay}, volumeMultiplier: ${volumeMultiplier}x`);

    // Network diagnostics: Parse URL to extract domain
    try {
      const urlObj = new URL(url);
      console.log(`[WebAudio Network] 🌐 Domain: ${urlObj.hostname}`);
      console.log(`[WebAudio Network] Protocol: ${urlObj.protocol}`);
      console.log(`[WebAudio Network] Full URL length: ${url.length} chars`);

      // Check if it's an AWS domain
      if (urlObj.hostname.includes('amazonaws.com') || urlObj.hostname.includes('aws')) {
        console.log(`[WebAudio Network] ⚠️ AWS domain detected - may be blocked in China mainland`);
      }

      // Check if it's a Cloudflare R2 domain
      if (urlObj.hostname.includes('r2.dev') || urlObj.hostname.includes('cloudflare')) {
        console.log(`[WebAudio Network] ✓ Cloudflare R2 domain - better accessibility in China`);
      }
    } catch (e) {
      console.warn('[WebAudio Network] Failed to parse URL:', e);
    }

    // Check browser online status
    if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
      console.log(`[WebAudio Network] Browser online status: ${navigator.onLine ? '✓ Online' : '✗ Offline'}`);
      if (!navigator.onLine) {
        console.error('[WebAudio Network] ✗ Browser is offline! Network requests will fail.');
      }
    }

    try {
      // Fetch audio file with timeout
      console.log('[WebAudio] Step 1/5: Fetching audio...');
      const fetchStart = performance.now();

      // Add 10-second timeout for fetch
      const FETCH_TIMEOUT = 10000; // 10 seconds
      const fetchWithTimeout = Promise.race([
        fetch(url),
        new Promise<Response>((_, reject) =>
          setTimeout(() => reject(new Error(`Fetch timeout after ${FETCH_TIMEOUT}ms - network too slow or blocked`)), FETCH_TIMEOUT)
        )
      ]);

      console.log(`[WebAudio Network] ⏱️ Starting fetch with ${FETCH_TIMEOUT}ms timeout...`);
      const response = await fetchWithTimeout;
      const fetchTime = performance.now() - fetchStart;

      console.log(`[WebAudio] ✓ Fetch complete in ${fetchTime.toFixed(0)}ms`);
      console.log(`[WebAudio] Response status: ${response.status} ${response.statusText}`);
      console.log(`[WebAudio] Response headers: Content-Type=${response.headers.get('content-type')}, Content-Length=${response.headers.get('content-length')}`);

      // Network diagnostics: Validate response
      const contentType = response.headers.get('content-type') || '';
      const contentLength = response.headers.get('content-length');

      // Check if fetch time is suspiciously long (>5 seconds)
      if (fetchTime > 5000) {
        console.warn(`[WebAudio Network] ⚠️ Fetch took ${fetchTime.toFixed(0)}ms (>5s) - network may be slow or unstable`);
      }

      // Validate content type
      if (!contentType.includes('audio') && !contentType.includes('mpeg') && !contentType.includes('mp3') && !contentType.includes('octet-stream')) {
        console.error(`[WebAudio Network] ✗ Invalid Content-Type: ${contentType} - expected audio/* or application/octet-stream`);
        console.error(`[WebAudio Network] This might be an error page (HTML) instead of audio data`);
      } else {
        console.log(`[WebAudio Network] ✓ Content-Type is valid: ${contentType}`);
      }

      // Check content length
      if (contentLength) {
        const sizeKB = parseInt(contentLength) / 1024;
        console.log(`[WebAudio Network] Expected download size: ${sizeKB.toFixed(2)} KB`);
        if (sizeKB < 1) {
          console.warn(`[WebAudio Network] ⚠️ Content size suspiciously small (${sizeKB.toFixed(2)} KB) - might be an error response`);
        }
      }

      if (!response.ok) {
        // Network diagnostics: Categorize HTTP errors
        if (response.status === 403) {
          console.error('[WebAudio Network] ✗ 403 Forbidden - URL may be expired or access denied');
          console.error('[WebAudio Network] For AWS Polly: presigned URLs expire after 1 hour');
        } else if (response.status === 404) {
          console.error('[WebAudio Network] ✗ 404 Not Found - resource does not exist');
        } else if (response.status >= 500) {
          console.error('[WebAudio Network] ✗ Server error - remote service is down');
        } else if (response.status === 0) {
          console.error('[WebAudio Network] ✗ Network error - request blocked by CORS, firewall, or network unavailable');
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const bufferStart = performance.now();
      console.log('[WebAudio Network] ⏬ Downloading response body...');
      const arrayBuffer = await response.arrayBuffer();
      const bufferTime = performance.now() - bufferStart;
      const actualSizeKB = arrayBuffer.byteLength / 1024;

      console.log(`[WebAudio] ✓ ArrayBuffer created in ${bufferTime.toFixed(0)}ms, size: ${arrayBuffer.byteLength} bytes (${actualSizeKB.toFixed(2)} KB)`);

      // Network diagnostics: Validate downloaded data
      if (arrayBuffer.byteLength === 0) {
        console.error('[WebAudio Network] ✗ Downloaded data is empty (0 bytes)');
        throw new Error('Empty audio data - network request returned no content');
      }

      if (actualSizeKB < 1) {
        console.warn(`[WebAudio Network] ⚠️ Downloaded data is very small (${actualSizeKB.toFixed(2)} KB) - might be corrupted`);
      }

      // Check if download speed was reasonable
      const downloadSpeedKBps = bufferTime > 0 ? (actualSizeKB / (bufferTime / 1000)) : 0;
      console.log(`[WebAudio Network] Download speed: ${downloadSpeedKBps.toFixed(2)} KB/s`);
      if (downloadSpeedKBps < 10 && bufferTime > 1000) {
        console.warn(`[WebAudio Network] ⚠️ Slow download speed (${downloadSpeedKBps.toFixed(2)} KB/s) - network may be throttled`);
      }

      // Initialize AudioContext if needed (use webkitAudioContext for older iOS)
      console.log('[WebAudio] Step 2/5: Initializing AudioContext...');
      if (!audioContext) {
        const AudioContextClass = (window.AudioContext || (window as any).webkitAudioContext);
        if (!AudioContextClass) {
          throw new Error('Web Audio API not supported');
        }
        audioContext = new AudioContextClass();
        gainNode = audioContext.createGain();
        gainNode.connect(audioContext.destination);
        console.log(`[WebAudio] ✓ New AudioContext created, state: ${audioContext.state}`);
      } else {
        console.log(`[WebAudio] ✓ Reusing existing AudioContext, state: ${audioContext.state}`);
      }

      // Resume AudioContext if suspended (important for iOS autoplay policy)
      console.log('[WebAudio] Step 3/5: Checking AudioContext state...');
      if (audioContext.state === 'suspended') {
        console.log('[WebAudio] AudioContext is suspended, attempting to resume...');
        const resumeStart = performance.now();
        await audioContext.resume();
        const resumeTime = performance.now() - resumeStart;
        console.log(`[WebAudio] ✓ AudioContext resumed in ${resumeTime.toFixed(0)}ms, new state: ${audioContext.state}`);
      } else {
        console.log(`[WebAudio] ✓ AudioContext already in ${audioContext.state} state`);
      }

      // Decode audio data
      console.log('[WebAudio] Step 4/5: Decoding audio data...');
      const decodeStart = performance.now();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const decodeTime = performance.now() - decodeStart;

      console.log(`[WebAudio] ✓ Audio decoded in ${decodeTime.toFixed(0)}ms`);
      console.log(`[WebAudio] Audio info: duration=${audioBuffer.duration.toFixed(2)}s, sampleRate=${audioBuffer.sampleRate}Hz, channels=${audioBuffer.numberOfChannels}`);

      // Create source and gain node for this playback
      console.log('[WebAudio] Step 5/5: Creating audio nodes and starting playback...');
      const source = audioContext.createBufferSource();
      const playbackGainNode = audioContext.createGain();

      source.buffer = audioBuffer;
      source.connect(playbackGainNode);
      playbackGainNode.connect(audioContext.destination);

      // Set volume with multiplier (can exceed 1.0 for amplification)
      const adjustedVolume = currentVolume * volumeMultiplier;
      playbackGainNode.gain.value = adjustedVolume;

      console.log(`[WebAudio] Volume configured: gain=${adjustedVolume.toFixed(2)} (base=${currentVolume}, multiplier=${volumeMultiplier}x)`);
      console.log(`[WebAudio] isMuted=${isMuted}, forcePlay=${forcePlay}`);

      // Set up ended callback
      source.onended = () => {
        const playbackTime = performance.now() - startTime;
        console.log(`[WebAudio] ✓ Playback ended normally after ${playbackTime.toFixed(0)}ms`);
        set({ isPlaying: false, playingText: '', ttsStatus: 'finished' });
        triggerEndCallback();
        try {
          playbackGainNode.disconnect();
          console.log('[WebAudio] Gain node disconnected');
        } catch (e) {
          console.warn('[WebAudio] Error disconnecting gain node:', e);
        }
      };

      // Start playback
      const playStart = performance.now();
      source.start(0);
      set({ isPlaying: true, ttsStatus: 'playing' });
      console.log(`[WebAudio] State updated: ttsStatus='playing', playingText='${get().playingText.substring(0, 30)}...'`);
      const playTime = performance.now() - playStart;

      const totalTime = performance.now() - startTime;
      console.log(`[WebAudio] ✅ Playback started successfully in ${playTime.toFixed(0)}ms`);
      console.log(`[WebAudio] Total setup time: ${totalTime.toFixed(0)}ms (fetch: ${fetchTime.toFixed(0)}ms, buffer: ${bufferTime.toFixed(0)}ms, decode: ${decodeTime.toFixed(0)}ms)`);
      console.log(`[WebAudio] Expected playback duration: ${audioBuffer.duration.toFixed(2)}s`);

      // Monitor playback state
      setTimeout(() => {
        if (audioContext) {
          console.log(`[WebAudio] Status check @ 100ms: state=${audioContext.state}, isPlaying=${get().isPlaying}`);
        }
      }, 100);

    } catch (error) {
      const errorTime = performance.now() - startTime;
      console.error(`[WebAudio] ✗ Error after ${errorTime.toFixed(0)}ms:`, error);
      console.error('[WebAudio] Error type:', error instanceof Error ? error.name : typeof error);
      console.error('[WebAudio] Error message:', error instanceof Error ? error.message : String(error));
      console.error('[WebAudio] Error stack:', error instanceof Error ? error.stack : 'N/A');

      // Network diagnostics: Categorize error types
      if (error instanceof Error) {
        const errorMsg = error.message.toLowerCase();

        if (errorMsg.includes('timeout')) {
          console.error('[WebAudio Network] ✗ TIMEOUT ERROR - Network request took too long (>10s)');
          console.error('[WebAudio Network] Possible causes:');
          console.error('[WebAudio Network] 1. Network is extremely slow');
          console.error('[WebAudio Network] 2. Domain is blocked/throttled (e.g., AWS blocked in China)');
          console.error('[WebAudio Network] 3. DNS resolution failed');
        } else if (errorMsg.includes('failed to fetch') || errorMsg.includes('network')) {
          console.error('[WebAudio Network] ✗ NETWORK ERROR - Could not establish connection');
          console.error('[WebAudio Network] Possible causes:');
          console.error('[WebAudio Network] 1. Domain is blocked by firewall/GFW');
          console.error('[WebAudio Network] 2. CORS policy blocking request');
          console.error('[WebAudio Network] 3. No internet connection');
          console.error('[WebAudio Network] 4. DNS lookup failed');
        } else if (errorMsg.includes('decode')) {
          console.error('[WebAudio Network] ✗ DECODE ERROR - Downloaded data is not valid audio');
          console.error('[WebAudio Network] Possible causes:');
          console.error('[WebAudio Network] 1. Received HTML error page instead of audio');
          console.error('[WebAudio Network] 2. Audio file is corrupted');
          console.error('[WebAudio Network] 3. Unsupported audio format');
        } else if (errorMsg.includes('403') || errorMsg.includes('forbidden')) {
          console.error('[WebAudio Network] ✗ 403 FORBIDDEN - Access denied');
          console.error('[WebAudio Network] Likely cause: AWS Polly presigned URL expired (>1 hour old)');
        } else if (errorMsg.includes('cors')) {
          console.error('[WebAudio Network] ✗ CORS ERROR - Cross-origin request blocked');
          console.error('[WebAudio Network] Server needs to allow cross-origin audio requests');
        }
      }

      // Check if browser is still online
      if (typeof navigator !== 'undefined' && 'onLine' in navigator && !navigator.onLine) {
        console.error('[WebAudio Network] ✗ Browser is now OFFLINE - network connection lost during request');
      }

      // Fallback to regular HTML Audio if Web Audio fails
      console.log('[WebAudio] 🔄 Attempting fallback to HTML Audio element...');
      try {
        const fallbackStart = performance.now();
        currentAudio = new Audio();
        currentAudio.src = url;
        currentAudio.volume = Math.min(currentVolume * volumeMultiplier, 1.0);
        currentAudio.muted = forcePlay ? false : isMuted;

        console.log(`[WebAudio Fallback] Configured: volume=${currentAudio.volume}, muted=${currentAudio.muted}`);

        currentAudio.onerror = (e) => {
          console.error('[WebAudio Fallback] Audio element error:', e);
          if (currentAudio) {
            console.error('[WebAudio Fallback] Error details:', {
              error: currentAudio.error,
              networkState: currentAudio.networkState,
              readyState: currentAudio.readyState,
            });
          }
        };

        currentAudio.onloadstart = () => console.log('[WebAudio Fallback] Loading started');
        currentAudio.onloadedmetadata = () => console.log('[WebAudio Fallback] Metadata loaded');
        currentAudio.oncanplay = () => console.log('[WebAudio Fallback] Can play');

        currentAudio.onended = () => {
          const fallbackTime = performance.now() - fallbackStart;
          console.log(`[WebAudio Fallback] ✓ Playback ended after ${fallbackTime.toFixed(0)}ms`);
          set({ isPlaying: false, playingText: '', ttsStatus: 'finished' });
          triggerEndCallback();
          currentAudio = null;
        };

        await currentAudio.play();
        const fallbackTime = performance.now() - fallbackStart;
        console.log(`[WebAudio Fallback] ✅ Playing with HTML Audio in ${fallbackTime.toFixed(0)}ms`);
        set({ isPlaying: true, ttsStatus: 'playing' });
        console.log(`[WebAudio Fallback] State updated: ttsStatus='playing', playingText='${get().playingText.substring(0, 30)}...'`);
      } catch (fallbackError) {
        console.error('[WebAudio Fallback] ✗ Fallback also failed:', fallbackError);
        console.error('[WebAudio Fallback] Fallback error type:', fallbackError instanceof Error ? fallbackError.name : typeof fallbackError);
        console.error('[WebAudio Fallback] Fallback error message:', fallbackError instanceof Error ? fallbackError.message : String(fallbackError));
        handlePlaybackError(error);
      }
    }
  };

  const executeTTSStream =  async (finalText: string, voiceId: string, needCache = true, speedOverride?: number) => {
    console.log("Starting TTS playback with PCM streaming");
    
    // Stop any ongoing playback
    cleanup();
    
    // Detect iOS/iPad
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // For iOS, use combined audio approach instead of streaming
    if (isIOS) {
      console.log("[iOS] Using combined audio approach for TTS");
      await executeTTSCombined(finalText, voiceId, needCache, speedOverride);
      return;
    }
    
    try {
      // Create new AbortController
      abortController = new AbortController();
      const signal = abortController.signal;
      
      // Reset all states
      isAborting = false;
      isStreamingTTS = true;
      isCollectingFinished = false;
      chunkQueue = [];
      
      // Get AudioContext
      initializeAudio();
              
      // Create main task that collects and plays simultaneously
      const mainTask = async () => {
        // Collector - asynchronously fill queue
        const collector = collectChunks(finalText, voiceId, signal, needCache, speedOverride);
        
        // Processor - wait for queue to have enough chunks and play
        try {
          // Initial wait - collect at least 3 chunks or stream ends
          console.log("Waiting for initial chunks...");
          while (chunkQueue.length < 2 && !isCollectingFinished && !signal.aborted) {
            console.log("Waiting for initial chunks... current queue length:", chunkQueue.length);
            await sleep(50); // Brief wait
          }
          
          if (signal.aborted) {
            console.log("Playback aborted while waiting for initial chunks");
            return;
          }
          
          console.log(`Initial buffer collected ${chunkQueue.length} chunks, starting playback...`);
          
          // Loop through all chunks - until collection is complete and queue is empty
          let playbackCount = 0;
          while ((!isCollectingFinished || chunkQueue.length > 0) && !signal.aborted) {
            playbackCount++;
            
            if (chunkQueue.length > 0) {
              // Copy all chunks in current queue
              const chunksToPlay = [...chunkQueue];
              chunkQueue = []; // Empty the queue
              
              console.log(`Playing batch ${playbackCount}: ${chunksToPlay.length} chunks`);
              
              // Play this batch of chunks
              await playChunks(chunksToPlay, signal);
              
              if (signal.aborted) break;
              
              console.log(`Finished playing batch ${playbackCount}`);
            } else {
              // If queue is empty but collection is not complete, wait
              console.log("Queue empty, waiting for more chunks...");
              await sleep(100);
            }
          }
          
          console.log("All audio playback complete");
          
        } catch (error) {
          if (!signal.aborted) {
            console.error("Error in audio playback:", error);
          }
        }
        
        // Ensure collector completes
        await collector;
        
        // Complete callback
        if (!signal.aborted) {
          triggerEndCallback();
          set({ isPlaying: false, ttsStatus: 'finished', playingText: '' });
        }
      };
      
      // Start main task
      mainTask();
      
    } catch (error) {
      console.error("Error setting up TTS playback:", error);
      cleanup();
    }
  };

  // Combined audio approach for iOS - collect all chunks then play
  const executeTTSCombined = async (finalText: string, voiceId: string, needCache = true, speedOverride?: number) => {
    console.log("[iOS] Starting combined TTS playback");
    
    try {
      // Create new AbortController
      abortController = new AbortController();
      const signal = abortController.signal;
      
      // Reset states
      isAborting = false;
      const allChunks: Uint8Array[] = [];
      
      // Collect all chunks first
      set({ ttsStatus: 'collecting' });
      
      let speakSpeed = speedOverride ?? 1.0;
      if (speedOverride == null) {
        if (voiceId === 'Cute_Elf') {
          speakSpeed = 0.95;
        }
        if (voiceId === 'Lovely_Girl') {
          speakSpeed = 1.3;
        }
      }
      
      console.log("[iOS] Collecting all TTS chunks...");
      for await (const { data, isLastChunk } of streamTTS({ text: finalText, voiceId, speed: speakSpeed, needCache })) {
        if (signal.aborted || isAborting) {
          console.log("[iOS] TTS collection aborted");
          set({ ttsStatus: 'aborted' });
          return;
        }
        
        if (isLastChunk) {
          console.log("[iOS] Received final chunk, skipping");
          continue;
        }
        
        allChunks.push(data);
      }
      
      console.log(`[iOS] Collected ${allChunks.length} chunks, creating combined audio...`);
      
      if (allChunks.length === 0 || signal.aborted) {
        set({ ttsStatus: 'finished' });
        return;
      }
      
      // Combine all chunks into one buffer
      const totalLength = allChunks.reduce((total, chunk) => total + chunk.length, 0);
      const combinedBuffer = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of allChunks) {
        combinedBuffer.set(chunk, offset);
        offset += chunk.length;
      }
      
      // Convert PCM to WAV format for better iOS compatibility
      const wavBlob = createWavFromPCM(combinedBuffer, 24000);
      const audioUrl = URL.createObjectURL(wavBlob);
      
      // Use HTML Audio element for iOS
      if (!unlockAudioElement) {
        unlockAudioElement = new Audio();
      }
      
      // Configure for iOS playback
      (unlockAudioElement as any).playsInline = true;
      unlockAudioElement.src = audioUrl;
      unlockAudioElement.muted = false;
      
      set({ isPlaying: true, ttsStatus: 'playing' });
      
      // Set up ended handler
      unlockAudioElement.onended = () => {
        URL.revokeObjectURL(audioUrl);
        triggerEndCallback();
        set({ isPlaying: false, ttsStatus: 'finished', playingText: '' });
      };
      
      // Play the combined audio
      await unlockAudioElement.play().catch((error) => {
        console.error("[iOS] Failed to play combined audio:", error);
        URL.revokeObjectURL(audioUrl);
        set({ isPlaying: false, ttsStatus: 'finished', playingText: '' });
      });

    } catch (error) {
      console.error("[iOS] Error in combined TTS playback:", error);
      set({ isPlaying: false, ttsStatus: 'finished', playingText: '' });
    }
  };
  
  // Helper function to create WAV from PCM data
  const createWavFromPCM = (pcmData: Uint8Array, sampleRate: number): Blob => {
    const dataSize = pcmData.length;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);
    let p = 0;
    
    const writeStr = (s: string) => {
      for (let i = 0; i < s.length; i++) view.setUint8(p++, s.charCodeAt(i));
    };
    const u16 = (v: number) => { view.setUint16(p, v, true); p += 2; };
    const u32 = (v: number) => { view.setUint32(p, v, true); p += 4; };
    
    // RIFF header
    writeStr("RIFF");
    u32(36 + dataSize);
    writeStr("WAVE");
    
    // fmt chunk
    writeStr("fmt ");
    u32(16); // fmt chunk size
    u16(1); // PCM format
    u16(1); // Mono
    u32(sampleRate);
    u32(sampleRate * 2); // Byte rate
    u16(2); // Block align
    u16(16); // Bits per sample
    
    // data chunk
    writeStr("data");
    u32(dataSize);
    
    // Copy PCM data
    const outputArray = new Uint8Array(buffer);
    outputArray.set(pcmData, 44);
    
    return new Blob([buffer], { type: 'audio/wav' });
  };
  
  // Simple sleep function
  function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  // Function to collect chunks - runs in a thread
  async function collectChunks(
    text: string,
    voiceId: string,
    signal: AbortSignal,
    needCache: boolean,
    speedOverride?: number,
  ): Promise<void> {
    let chunkCount = 0;
    
    try {
      let speakSpeed = speedOverride ?? 1.0;
      if (speedOverride == null) {
        if (voiceId === 'Cute_Elf') {
          speakSpeed = 0.95;
        }
        if (voiceId === 'Lovely_Girl') {
          speakSpeed = 1.3;
        }
      }

      set({ ttsStatus: 'collecting' });
      for await (const { data, isLastChunk } of streamTTS({ text, voiceId, speed: speakSpeed, needCache })) {
        // Check if cancelled
        if (signal.aborted || isAborting) {
          console.log("Chunk collection aborted");
          set({ ttsStatus: 'aborted' });
          return;
        }
        
        // Skip final summary chunk
        if (isLastChunk) {
          console.log("Received final chunk with summary info, skipping");
          continue;
        }
        
        chunkCount++;
        console.log(`Received PCM chunk ${chunkCount}, adding to queue (length: ${chunkQueue.length + 1})`);
        
        // Add to queue
        chunkQueue.push(data);
      }
      
      console.log(`Finished collecting all ${chunkCount} chunks`);
      isCollectingFinished = true;
    } catch (error) {
      if (!signal.aborted && !isAborting) {
        console.error("Error collecting chunks:", error);
      }
      isCollectingFinished = true;
    }
  }
    
  // Play a group of chunks
  async function playChunks(chunks: Uint8Array[], signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!audioContext || signal.aborted || isAborting) {
        resolve();
        console.log("In PlayChunks resolved, mark 1");
        return;
      }
      
      try {
        if (chunks.length === 0) {
          resolve();
          console.log("In PlayChunks resolved, mark 2");
          return;
        }
        
        // Merge all chunks
        const totalLength = chunks.reduce((total, chunk) => total + chunk.length, 0);
        const combinedBuffer = new Uint8Array(totalLength);
        
        let offset = 0;
        for (let i = 0; i < chunks.length; i++) {
          combinedBuffer.set(chunks[i], offset);
          offset += chunks[i].length;
        }
        
        // Convert to PCM audio
        const pcmData = new Int16Array(combinedBuffer.buffer);
        const audioBuffer = audioContext.createBuffer(1, pcmData.length, 24000);
        const channelData = audioBuffer.getChannelData(0);
        
        console.log("In PlayChunks, convert to float32");
        // Convert Int16 to Float32
        for (let i = 0; i < pcmData.length; i++) {
          channelData[i] = pcmData[i] / 32768.0;  // Convert int16 to float32 (-1 to 1)
        }
        
        // Create audio source
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(gainNode!);
        
        // Set end callback
        source.onended = () => {
          if (signal.aborted) {
            reject(new Error("Playback aborted"));
          } else {
            resolve();
          }
        };
        
        // Register abort handling
        signal.addEventListener('abort', () => {
          try {
            source.stop();
            source.disconnect();
          } catch (e) {
            // Ignore errors
          }
          reject(new Error("Playback aborted"));
        }, { once: true });
        
        set({ isPlaying: true, ttsStatus: 'playing' });
        // Start playback
        source.start();
        console.log("In PlayChunks, start playback, audio duration:", audioBuffer.duration);

        setTimeout(() => {
          if (audioContext && audioContext.state === 'suspended') {
            console.warn('AudioContext was suspended. Attempting to resume...');
            // Try to resume the audio context instead of toggling mute
            audioContext.resume().then(() => {
              console.log('AudioContext resumed successfully');
            }).catch((err) => {
              console.error('Failed to resume AudioContext:', err);
              set({ isPlaying: false });
              reject(new Error("AudioContext was suspended and could not be resumed"));
            });
          }
        }, 50);
      } catch (error) {
        console.error("In PlayChunks, Error processing chunks for playback:", error);
        resolve(); // Resolve even on error to continue flow
      }
    });
  }  

  return {
    isPlaying: false,
    isMuted,
    voiceId: '',
    playingType: 'assistant',
    playingId: '',
    playingText: '',
    ttsStatus: 'idle',

    play: (url: string, forcePlay = false, volumeMultiplier = 1.0) => {
      const playStartTime = performance.now();
      console.log(`[Audio] play() called with URL: ${url.substring(0, 50)}...`);

      // Check if playback should be skipped
      if (shouldSkipPlayback(forcePlay)) return;

      // Save current playingText before cleanup
      const currentPlayingText = get().playingText;

      // Clean up previous audio resources
      cleanupAudioElement();
      cleanup();

      // Restore playingText after cleanup
      set({ playingText: currentPlayingText });

      // If volumeMultiplier > 1.0, use Web Audio API for gain control
      if (volumeMultiplier > 1.0) {
        console.log(`[Audio] Using Web Audio API for volume boost: ${volumeMultiplier}x`);
        playWithWebAudio(url, forcePlay, volumeMultiplier);
        return;
      }

      try {
        // Standard playback for normal volume
        currentAudio = new Audio();
        currentAudio.preload = "auto";
        // Use 'canplay' instead of 'canplaythrough' for faster playback start
        // 'canplay' fires when some data is available, reducing perceived latency
        currentAudio.oncanplay = () => {
          const readyTime = performance.now() - playStartTime;
          console.log(`[Audio] Ready to play after ${readyTime.toFixed(0)}ms (canplay event)`);
          currentAudio?.play().catch(handlePlaybackError);
        };
        currentAudio.onloadstart = () => console.log("[Audio] Loading started");
        currentAudio.onloadedmetadata = () => {
          const metadataTime = performance.now() - playStartTime;
          console.log(`[Audio] Metadata loaded after ${metadataTime.toFixed(0)}ms`);
        };
        currentAudio.onloadeddata = () => {
          const dataTime = performance.now() - playStartTime;
          console.log(`[Audio] Data loaded after ${dataTime.toFixed(0)}ms`);
        };
        currentAudio.onplaying = () => {
          const playingTime = performance.now() - playStartTime;
          console.log(`[Audio] ▶️ Actually playing after ${playingTime.toFixed(0)}ms from play() call`);
        };
        currentAudio.src = url;

        currentAudio.volume = currentVolume;
        currentAudio.muted = forcePlay ? false : isMuted;

        currentAudio.onended = () => {
          set({ isPlaying: false, playingText: '', ttsStatus: 'finished' });
          triggerEndCallback();
          currentAudio = null;
        };

        currentAudio.play().catch(handlePlaybackError);
        set({ isPlaying: true, ttsStatus: 'playing' });
        console.log(`[Audio] State updated: ttsStatus='playing', playingText='${get().playingText.substring(0, 30)}...'`);
      } catch (error) {
        handlePlaybackError(error);
      }
    },

    pause: () => {
      // Prioritize checking and pausing HTML Audio element
      if (currentAudio) {
        currentAudio.pause();
      }
      
      // Also pause AudioContext playback
      if (audioContext) {
        audioContext.suspend();
      }
      
      set({ isPlaying: false });
    },

    stop: () => {
      cleanupAudioElement();
      cleanup();
    },

    unload: () => {
      try {
        // Same as stop, but more thorough resource cleanup
        if (currentAudio) {
          try {
            currentAudio.pause();
            currentAudio.src = "";
            currentAudio.remove(); // More thorough element removal
            currentAudio = null;
          } catch (error) {
            console.error('Error unloading audio:', error);
          }
        }
        
        cleanup();
      } catch (error) {
        console.error('Error in unload:', error);
        // Ensure basic resource cleanup even on error
        currentAudio = null;
        cleanup();
      }
    },

    setVolume: (volume: number) => {
      currentVolume = Math.max(0, Math.min(1, volume));
      if (gainNode) {
        gainNode.gain.value = currentVolume;
      }
    },

    mute: () => {
      isMuted = true;
      // Save to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEYS.AUDIO_SETTING_MUTED, 'true');
      }
      set({ isMuted: true });
      if (gainNode) {
        gainNode.gain.value = 0;
      }
    },

    unmute: () => {
      isMuted = false;
      // Save to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEYS.AUDIO_SETTING_MUTED, 'false');
      }
      set({ isMuted: false });
      if (gainNode) {
        gainNode.gain.value = currentVolume;
      }
    },
    
    toggleMute: () => {
      if (isMuted) {
        get().unmute();
      } else {
        get().mute();
        get().interruptAudio();
      }
      report(ReportEvent.USER_MANUAL_VOLUME_BUTTON, {
        isMute: isMuted,
      })
    },

    setOnEnded: (callback: () => void) => {
      onEndedCallback = callback;
    },
    // TTS chat content in chat scenarios, compared to playtts, only keeps mute off, no cache
    chatTTS: async (text: string) => {
      const isMuted = get().isMuted;
      if (isMuted) {
        return;
      }
      get().interruptAudio();
      const voiceId = await get().initVoiceId(CurrentVoiceId);
      set({ playingType: 'pet' });
      
      // First remove emoji
      const cleanText = stripNonTextCharacters(text);
      
      // Detect if it contains LaTeX (using processed text)
      if (hasLatex(cleanText)) {
        console.log('Detected LaTeX in text, processing...');
        
        // Try LLM processing
        const llmProcessedText = await convertLatexToNaturalLanguage(cleanText);
        
        if (!llmProcessedText) {
          console.log('LLM processing failed, skipping TTS for LaTeX content');
          return;
        }
         
         // Continue TTS process using LLM processed text
         await executeTTSStream(llmProcessedText, voiceId, false);
       } else {
         // No LaTeX, use processed text directly for TTS
         await executeTTSStream(cleanText, voiceId, false);
       }
    },

    // forcePlay forces playback, unaffected by mute state or text length, remove all playback-affecting factors at code level
    playTTS: async (text: string, options?: PlayTTSOptions) => {
      const { voiceId: voiceIdParam, forcePlay = false, needCache = true, speed } = options || {};
      const user = useAuthStore.getState().user;

      if (!user) {
        set({ playingText: '', ttsStatus: 'idle' });
        return;
      }
      const voiceId = await get().initVoiceId(voiceIdParam || CurrentVoiceId);

      // Set playing text for button identification (use original text passed in)
      set({ playingText: text, ttsStatus: 'collecting' });

      // Check if playback should be skipped
      if (shouldSkipPlayback(forcePlay)) return;

      // First remove emoji
      const cleanText = stripNonTextCharacters(text);

      // Process text: replace dashes with commas
      const processedText = convertDashesToCommas(cleanText);
      const trimmedText = trimTextToFirstSentence(processedText);
      // Check text: if more than 3 sentences and forcePlay is false, don't play
      if (!trimmedText && !forcePlay) {
        console.log('Text too long (>3 sentences), skipping TTS');
        set({ playingText: '', ttsStatus: 'idle' });
        return;
      }

      // First check original text cache (using processed text)
      const originalCachedUrl = await checkTTSCache(processedText, voiceId);
      if (originalCachedUrl && needCache) {
        console.log('Found cache for original text');
        get().play(originalCachedUrl, forcePlay);
        return;
      }
      
      // Detect if it contains LaTeX (using processed text)
      if (hasLatex(processedText)) {
        console.log('Detected LaTeX in text, processing...');
        
        // Try LLM processing
        const llmProcessedText = await convertLatexToNaturalLanguage(processedText);
        
        if (!llmProcessedText) {
          console.log('LLM processing failed, skipping TTS for LaTeX content');
          return;
        }
        
         // Use LLM processed text as final text
         console.log('Using LLM processed text for TTS');
         
         // Continue TTS process using LLM processed text
         await executeTTSStream(llmProcessedText, voiceId || CurrentVoiceId, needCache, speed);
       } else {
         // No LaTeX, use processed text directly for TTS
         await executeTTSStream(processedText, voiceId || CurrentVoiceId, needCache, speed);
       }
    },

    interruptAudio: () => {
      get().stop();
      triggerEndCallback();
    },

    getAndClearTTSTiming: () => {
      const timing = lastTTSTiming;
      lastTTSTiming = null;
      return timing;
    },

    // Preload audio without playing - for warming up cache
    preloadPollyTTS: async (text: string, voiceId = 'Joanna') => {
      console.log(`[Polly TTS] 🔄 Preloading audio for: "${text.substring(0, 30)}..."`);

      // First remove emoji
      const cleanText = stripNonTextCharacters(text);

      // Check text: if more than 3 sentences, don't preload
      const trimmedText = trimTextToFirstSentence(cleanText);
      if (!trimmedText) {
        console.log('[Polly TTS] ⏭️  Text too long, skipping preload');
        return;
      }

      try {
        // Quick check: is it already in memory cache?
        const cacheKey = getCacheKey(trimmedText, voiceId);
        if (memoryCache.has(cacheKey)) {
          console.log('[Polly TTS] ✓ Already in memory cache, no preload needed');
          return;
        }

        // Not in memory cache, start async preload
        console.log('[Polly TTS] ⚡ Starting background preload...');

        // Don't await - let it run in background
        (async () => {
          try {
            // Check Supabase cache (with timeout)
            const cachedUrl = await checkTTSCache(trimmedText, voiceId);

            if (cachedUrl) {
              console.log(`[Polly TTS] ✓ Preload: Found in Supabase, cached locally`);
              return;
            }

            // Not in any cache, generate it
            const { apiClient } = await import('@/lib/api-client');
            const response = await apiClient.post('/api/tts/polly', {
              text: trimmedText,
              voiceId,
            });

            if (!response.ok) {
              throw new Error('Preload: Failed to get TTS URL');
            }

            const data = await response.json();

            // Save to local caches
            saveToCaches(trimmedText, voiceId, data.url);
            console.log(`[Polly TTS] ✅ Preload complete: "${trimmedText.substring(0, 30)}..."`);

            // Also store to R2 in background
            (async () => {
              try {
                const storeResponse = await apiClient.post('/api/tts/polly/store', {
                  audioUrl: data.url,
                  text: trimmedText,
                });

                if (storeResponse.ok) {
                  const { url: persistentUrl } = await storeResponse.json();

                  await supabase
                    .from('tts_cache')
                    .insert([{ text: trimmedText, audio_url: persistentUrl }]);

                  saveToCaches(trimmedText, voiceId, persistentUrl);
                }
              } catch (error) {
                console.warn('[Polly TTS] Preload storage failed (non-critical):', error);
              }
            })();
          } catch (error) {
            console.warn('[Polly TTS] ⚠️  Preload failed (will retry on play):', error);
          }
        })();
      } catch (error) {
        console.warn('[Polly TTS] Preload error:', error);
      }
    },

    playPollyTTS: async (text: string, voiceId = 'Joanna', forcePlay = false) => {
      const perfStart = performance.now();
      console.log('[Polly TTS] Starting playback process');

      // Set playing text for button identification
      set({ playingText: text, ttsStatus: 'collecting' });

      // Check if playback should be skipped
      if (shouldSkipPlayback(forcePlay)) return;

      // First remove emoji
      const cleanText = stripNonTextCharacters(text);

      // Check text: if more than 3 sentences, don't play
      const trimmedText = trimTextToFirstSentence(cleanText);
      if (!trimmedText) {
        console.log('Text too long (>3 sentences), skipping TTS');
        set({ playingText: '', ttsStatus: 'idle' });
        return;
      }

      try {
        // Quick check: memory cache
        const cacheKey = getCacheKey(trimmedText, voiceId);

        if (memoryCache.has(cacheKey)) {
          const url = memoryCache.get(cacheKey)!;
          console.log(`[Polly TTS] ✓ Memory cache hit, total time: ${(performance.now() - perfStart).toFixed(0)}ms`);
          get().play(url, forcePlay, 1.5);
          return;
        }

        // Not in memory cache - use aggressive parallel strategy
        console.log('[Polly TTS] ⚡ Memory cache miss, starting parallel fetch...');

        // Import apiClient once
        const { apiClient } = await import('@/lib/api-client');

        // Start BOTH cache check and API call in parallel
        const cachePromise = (async () => {
          const supabaseUrl = await supabase
            .from('tts_cache')
            .select('*')
            .eq('text', trimmedText)
            .eq('voice_id', voiceId)
            .maybeSingle()
            .then(({ data }) => data?.audio_url || null);

          return { source: 'supabase' as const, url: supabaseUrl };
        })();

        const apiPromise = (async () => {
          const response = await apiClient.post('/api/tts/polly', {
            text: trimmedText,
            voiceId,
          });

          if (!response.ok) {
            throw new Error('Failed to get TTS URL');
          }

          const data = await response.json();
          return { source: 'api' as const, url: data.url };
        })();

        // Race them - use whichever completes first
        const result = await Promise.race([cachePromise, apiPromise]);

        const totalTime = performance.now() - perfStart;
        console.log(`[Polly TTS] ✓ ${result.source === 'supabase' ? 'Supabase' : 'API'} won the race! Total time: ${totalTime.toFixed(0)}ms`);

        if (result.url) {
          // Save to all caches
          saveToCaches(trimmedText, voiceId, result.url);

          // Start playing immediately
          get().play(result.url, forcePlay, 1.5);

          // If API won, still wait for Supabase result to update cache if available
          if (result.source === 'api') {
            cachePromise.then(({ url }) => {
              if (url) {
                saveToCaches(trimmedText, voiceId, url);
                console.log('[Polly TTS] Also found in Supabase, updated caches');
              }
            }).catch(() => {});
          }
        } else {
          // Result was null (shouldn't happen but handle gracefully)
          console.warn('[Polly TTS] Race result was null, falling back to sequential');

          // Try the other promise
          const fallback = result.source === 'supabase' ? await apiPromise : await cachePromise;
          if (fallback.url) {
            saveToCaches(trimmedText, voiceId, fallback.url);
            get().play(fallback.url, forcePlay, 1.5);
          } else {
            throw new Error('Both sources returned null');
          }
        }

        // Asynchronously store audio to R2 (only if from API)
        if (result.source === 'api' && result.url) {
          (async () => {
            try {
              const storeResponse = await apiClient.post('/api/tts/polly/store', {
                audioUrl: result.url,
                text: trimmedText,
              });

              if (!storeResponse.ok) {
                throw new Error('Failed to store audio');
              }

              const { url: persistentUrl } = await storeResponse.json();

              // Update Supabase cache
              await supabase
                .from('tts_cache')
                .insert([
                  {
                    text: trimmedText,
                    audio_url: persistentUrl,
                  },
                ]);

              // Update all caches with persistent URL
              saveToCaches(trimmedText, voiceId, persistentUrl);

              console.log('Audio stored and cached successfully');
            } catch (error) {
              console.error('Error storing audio:', error);
              // Storage failure doesn't affect playback, so just log error
            }
          })();
        }
      } catch (error) {
        console.error('Error in playPollyTTS:', error);
        handlePlaybackError(error);
      }
    },

    setPlayingType: (type: PLAYING_TYPE) => {
      set({ playingType: type });
    },
    
    unlockAudioForIOS: async () => {
      // Check if already unlocked or not on iOS
      if (iosAudioUnlocked) {
        console.log("[iOS Audio] Already unlocked");
        return;
      }
      
      // Detect iOS/iPad
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      if (!isIOS) {
        iosAudioUnlocked = true; // Mark as unlocked for non-iOS devices
        return;
      }
      
      try {
        // Create audio element for unlocking if not exists
        if (!unlockAudioElement) {
          unlockAudioElement = new Audio();
        }
        
        // iOS requires: playsInline + user gesture to unlock
        unlockAudioElement.muted = true; // Start muted for autoplay
        (unlockAudioElement as any).playsInline = true;
        unlockAudioElement.loop = false;
        unlockAudioElement.src = makeTinySilentWavDataURL(); // Tiny silent WAV
        
        // Try to play to unlock
        await unlockAudioElement.play().catch(() => {});
        
        // Immediately pause and unmute: ready for real TTS later
        unlockAudioElement.pause();
        unlockAudioElement.currentTime = 0;
        unlockAudioElement.muted = false;
        
        iosAudioUnlocked = true;
        console.log("[iOS Audio] Successfully unlocked audio playback");
      } catch (e) {
        console.warn("[iOS Audio] Failed to unlock:", e);
      }
    },

    initVoiceId: async (voiceIdParam: string) => {
      let voiceId = voiceIdParam === CurrentVoiceId ? '' : voiceIdParam;
      if (voiceId === '') {
        // 向server查询voiceId
        const storedVoiceId = get().voiceId;  
        const user = useAuthStore.getState().user;
        if (!user) {
          voiceId = CurrentVoiceId;
        } else {
          if (storedVoiceId === '') {
            const { data, error } = await supabase
              .from('profiles')
              .select('voice_id')
              .eq('id', user.id)
              .single();
  
            if (error || !data) {
              voiceId = CurrentVoiceId;
            } else {
              voiceId = data?.voice_id || CurrentVoiceId;
              set({ voiceId: voiceId });
            };
          } else {
            voiceId = storedVoiceId;
          }
        }
      }
      console.log('finally initVoiceId voiceId:', voiceId);
      return voiceId;
    }
  };
});

export default useAudioStore;
